import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Mail, CreditCard, RefreshCw, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const KindMeta = {
  webhook_errors: {
    label: "Stripe webhook failures",
    icon: AlertTriangle,
    color: "text-rose-600",
    bg: "bg-rose-50",
    border: "border-rose-200",
  },
  email_failures: {
    label: "Email delivery failures",
    icon: Mail,
    color: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-200",
  },
  stripe_errors: {
    label: "Stripe API errors",
    icon: CreditCard,
    color: "text-fuchsia-600",
    bg: "bg-fuchsia-50",
    border: "border-fuchsia-200",
  },
};

const MetricCard = ({ kind, value }) => {
  const meta = KindMeta[kind];
  const Icon = meta.icon;
  const isZero = value === 0;
  return (
    <Card
      className={`p-5 ${meta.bg} ${meta.border} border`}
      data-testid={`health-metric-${kind}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide font-semibold text-slate-600">
            {meta.label}
          </p>
          <p
            className={`mt-2 text-4xl font-display font-semibold ${isZero ? "text-slate-900" : meta.color}`}
            data-testid={`health-count-${kind}`}
          >
            {value}
          </p>
          <p className="mt-1 text-xs text-slate-500">last 24 hours</p>
        </div>
        <Icon className={`h-6 w-6 ${meta.color} shrink-0`} />
      </div>
    </Card>
  );
};

const EventRow = ({ ev, kind }) => {
  const details = ev.details || {};
  return (
    <div
      className="py-3 px-4 border-b last:border-b-0 hover:bg-slate-50"
      data-testid={`health-event-row-${kind}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900 truncate" title={ev.message}>
            {ev.message}
          </p>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
            {details.event_type && (
              <span>event: <code className="text-slate-700">{details.event_type}</code></span>
            )}
            {details.event_id && (
              <span>id: <code className="text-slate-700">{details.event_id}</code></span>
            )}
            {details.error_class && (
              <Badge variant="outline" className="h-5 text-[10px] px-1.5">
                {details.error_class}
              </Badge>
            )}
            {details.pickup_id && (
              <span>pickup: <code className="text-slate-700">{details.pickup_id}</code></span>
            )}
            {details.subkind && (
              <span>subkind: <code className="text-slate-700">{details.subkind}</code></span>
            )}
            {details.to && <span>to: {Array.isArray(details.to) ? details.to.join(", ") : details.to}</span>}
            {details.subject && <span className="truncate max-w-xs">subject: {details.subject}</span>}
          </div>
        </div>
        <span className="text-xs text-slate-500 whitespace-nowrap">{formatTime(ev.created_at)}</span>
      </div>
    </div>
  );
};

const EventList = ({ kind, events }) => {
  const meta = KindMeta[kind];
  return (
    <Card className="overflow-hidden" data-testid={`health-list-${kind}`}>
      <div className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2">
        <meta.icon className={`h-4 w-4 ${meta.color}`} />
        <h3 className="font-display font-semibold text-slate-900 text-sm">
          {meta.label} <span className="text-slate-500 font-normal">— recent 20</span>
        </h3>
      </div>
      {events.length === 0 ? (
        <div
          className="px-4 py-10 text-center"
          data-testid={`health-empty-${kind}`}
        >
          <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
          <p className="mt-3 text-sm font-medium text-slate-700">All clear</p>
          <p className="text-xs text-slate-500">No {meta.label.toLowerCase()} in the last 30 days.</p>
        </div>
      ) : (
        <div className="max-h-[360px] overflow-y-auto">
          {events.map((ev, i) => (
            <EventRow key={`${kind}-${i}`} ev={ev} kind={kind} />
          ))}
        </div>
      )}
    </Card>
  );
};

export default function HealthPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await axios.get(`${API}/admin/health`, {
        headers: { "X-Admin-Token": token },
      });
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load health data");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const counts = data?.counts_24h || { webhook_errors: 0, email_failures: 0, stripe_errors: 0 };

  return (
    <div className="space-y-6" data-testid="health-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-display font-semibold text-slate-900">System Health</h2>
          <p className="text-sm text-slate-500">
            Silent-failure events across Stripe webhooks, emails, and payments.
            {data?.generated_at && (
              <>
                {" "}<span className="text-slate-400">Updated {formatTime(data.generated_at)}</span>
              </>
            )}
          </p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          size="sm"
          disabled={loading}
          data-testid="health-refresh-btn"
          className="gap-2 self-start sm:self-auto"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {err && (
        <div
          className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
          data-testid="health-error"
        >
          {err}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard kind="webhook_errors" value={counts.webhook_errors} />
        <MetricCard kind="email_failures" value={counts.email_failures} />
        <MetricCard kind="stripe_errors" value={counts.stripe_errors} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <EventList kind="webhook_errors" events={data?.webhook_errors || []} />
        <EventList kind="email_failures" events={data?.email_failures || []} />
        <EventList kind="stripe_errors" events={data?.stripe_errors || []} />
      </div>
    </div>
  );
}
